const { SITE_BASE_URL } = require('./brokerConnectionSource');
const { ogImageUrlForPath } = require('./ogTitleCard');
const { NOINDEX_SITE_ROUTES, PUBLIC_SITE_ROUTES } = require('./siteRoutes');

const PAGE_MANAGED_METADATA_PATHS = new Set([
  '/',
  '/sales',
  '/marketplace',
  '/sales-marketplace',
  '/pricing',
  '/courses/ai-trading-bootcamp',
  '/courses/ai-trading-bootcamp/self-paced',
  '/live-algo-lab',
  '/upgrade-to-pro',
  '/blog',
  '/blog/using-ai-to-make-ai-profitable-swing-trading-bot-in-seconds',
  '/compare',
]);

const ROBOTS_DISALLOW_PREFIXES = Object.freeze([
  '/api/',
  '/checkout',
  '/set-password',
  '/signup-survey',
  '/auto-login-*',
  '/modal-test',
  '/onboarding',
  '/oauth/',
  '/ai-agent',
  '/dashboard',
  '/my-bots',
  '/login',
  '/account-settings',
  '/add-bot',
  '/backtest',
  '/deploy',
  '/brokerage-accounts',
  '/my-backtests',
  '/my-deployments',
  '/my-strategies',
  '/my-published-strategies',
  '/user_profile',
  '/affiliate/dashboard',
]);

// Boundary-aware matching keeps nested app routes private without catching
// public names that merely share a prefix (for example /backtesting-guides).
const PRIVATE_NOINDEX_PATH_PREFIXES = Object.freeze([
  ...ROBOTS_DISALLOW_PREFIXES.filter((prefix) => !prefix.includes('*')).map((prefix) =>
    prefix.replace(/\/$/, ''),
  ),
  '/marketplace/account',
  '/lp',
  '/community',
  '/posts',
  '/profile',
  '/share',
]);

const ROBOTS_ALLOW_PUBLIC_PREFIXES = Object.freeze([
  '/',
  '/sales',
  '/marketplace',
  '/pricing',
  '/blog',
  '/compare',
  '/guides',
  '/brokers',
  '/courses',
  '/agents',
  '/case-studies',
]);

function normalizePathname(pathname) {
  if (!pathname) return '/';
  const value = String(pathname);
  if (value === '/') return '/';
  return value.replace(/\/+$/, '') || '/';
}

function isPageManagedMetadataPath(pathname) {
  const path = normalizePathname(pathname);
  return (
    PAGE_MANAGED_METADATA_PATHS.has(path) ||
    path.startsWith('/compare/') ||
    path === '/guides' ||
    path.startsWith('/guides/') ||
    // ComparisonPage also renders the broker pages' title, canonical and JSON-LD.
    path === '/brokers' ||
    path.startsWith('/brokers/')
  );
}

function getNoindexRoute(pathname) {
  const path = normalizePathname(pathname);
  if (
    /^\/courses-clarity(?:\/|$)/.test(path) ||
    /^\/courses\/ai-trading-bootcamp\/(control|agentic|v2|clarity-home)$/.test(path)
  ) {
    return {
      path,
      title: 'Bootcamp page review | BotSpot',
      description: 'Course page review.',
      robots: 'noindex, follow',
      sitemap: false,
    };
  }
  const privateRoute =
    PRIVATE_NOINDEX_PATH_PREFIXES.some(
      (prefix) => path === prefix || path.startsWith(`${prefix}/`),
    ) ||
    /^\/auto-login-[^/]+(?:\/|$)/.test(path) ||
    /^\/marketplace\/strategy\/[^/]+\/settings(?:\/|$)/.test(path);
  return (
    NOINDEX_SITE_ROUTES.find((route) => route.path === path) ||
    (privateRoute
      ? {
          path,
          title: 'BotSpot account',
          description: 'BotSpot account and application page.',
          robots: 'noindex, follow',
          sitemap: false,
        }
      : null) ||
    (path.startsWith('/lp/')
      ? {
          path,
          title: 'BotSpot landing page',
          description: 'Paid BotSpot landing page. This URL is not a public marketing index page.',
          robots: 'noindex, follow',
          sitemap: false,
        }
      : null)
  );
}

function isNoindexPath(pathname) {
  return Boolean(getNoindexRoute(pathname));
}

function getPublicPageSeo(pathname) {
  const path = normalizePathname(pathname);
  const route = PUBLIC_SITE_ROUTES.find((item) => item.path === path);
  if (!route) return null;
  return {
    ...route,
    canonical: `${SITE_BASE_URL}${path === '/' ? '/' : path}`,
    image: route.image || ogImageUrlForPath(path),
    robots: route.robots || 'index, follow',
    heading: route.content?.heading || route.title,
  };
}

module.exports = {
  PAGE_MANAGED_METADATA_PATHS,
  PRIVATE_NOINDEX_PATH_PREFIXES,
  ROBOTS_ALLOW_PUBLIC_PREFIXES,
  ROBOTS_DISALLOW_PREFIXES,
  getNoindexRoute,
  getPublicPageSeo,
  isNoindexPath,
  isPageManagedMetadataPath,
  normalizePathname,
};
